import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const CANADIAN_TIMEZONES = [
  "America/Toronto", "America/Vancouver", "America/Edmonton",
  "America/Winnipeg", "America/Halifax", "America/St_Johns",
  "America/Regina", "America/Moncton", "America/Yellowknife",
  "America/Whitehorse", "America/Iqaluit", "America/Montreal",
  "America/Atikokan", "America/Blanc-Sablon", "America/Cambridge_Bay",
  "America/Creston", "America/Dawson", "America/Dawson_Creek",
  "America/Fort_Nelson", "America/Glace_Bay", "America/Goose_Bay",
  "America/Inuvik", "America/Pangnirtung", "America/Rankin_Inlet",
  "America/Resolute", "America/Swift_Current",
];

const CANADIAN_LOCALE_REGEX = /(?:^|[-_])CA(?:$|[-_])/i;

function isCanadianLocale(): boolean {
  try {
    const localeFromIntl = Intl.DateTimeFormat().resolvedOptions().locale;
    const localeFromNavigator = typeof navigator !== "undefined"
      ? [navigator.language, ...(navigator.languages || [])]
      : [];

    return [localeFromIntl, ...localeFromNavigator]
      .filter(Boolean)
      .some((locale) => CANADIAN_LOCALE_REGEX.test(locale));
  } catch {
    return false;
  }
}

function isCanadianTimezone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return false;
    return CANADIAN_TIMEZONES.includes(tz) || tz.startsWith("Canada/");
  } catch {
    return false;
  }
}

function getEnglishFlag(): string {
  return isCanadianTimezone() || isCanadianLocale() ? "🇨🇦" : "🇺🇸";
}

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const englishFlag = useMemo(getEnglishFlag, []);

  const languages = useMemo(() =>
    SUPPORTED_LANGUAGES.map(l => l.code === "en" ? { ...l, flag: englishFlag } : l),
    [englishFlag]
  );

  const currentLang = languages.find(l => i18n.language?.startsWith(l.code)) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-11 px-2 gap-1.5 text-base" aria-label="Change language">
          <span className="text-lg leading-none">{currentLang.flag}</span>
          <span className="hidden sm:inline text-sm font-medium">{currentLang.code.toUpperCase()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            className={`gap-2 ${currentLang.code === lang.code ? "bg-accent font-semibold" : ""}`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
